import { inject, injectable } from 'tsyringe';
import type { TransportResponse } from 'atemoya-contracts';
import { ITransportService, TransportRequestTypes } from './ITransportService';

declare global {
    interface Window {
        GAME_NAME: string;
    }
}

enum RestEndpoint {
    INIT = 'init',
    ACTION = 'action',
}

@injectable()
export class RestService implements ITransportService {
    private _baseUrl: string;
    private _isConnected: boolean = false;
    private _timeout: number = 30000;
    private _gameName: string;

    constructor(
        @inject('REST_URL') url: string,
        @inject('TRANSPORT_TIMEOUT') timeout?: number,
        @inject('GAME_NAME') gameName?: string
    ) {
        this._gameName = gameName ?? (typeof window !== 'undefined' ? window.GAME_NAME : '') ?? '';
        this._baseUrl = url.replace('ws://', 'http://').replace('wss://', 'https://');
        if (timeout) {
            this._timeout = timeout;
        }
    }

    public async connect(): Promise<void> {
        if (!this._isConnected) {
            this._isConnected = true;
        }
        return Promise.resolve();
    }

    public disconnect(): void {
        this._isConnected = false;
    }

    public async send(
        type: TransportRequestTypes,
        payload: Record<string, any>,
        clientRequestId: string
    ): Promise<TransportResponse> {
        if (!this._isConnected) {
            await this.connect();
        }

        const isInit = type === TransportRequestTypes.INIT;
        const endpoint = isInit ? RestEndpoint.INIT : RestEndpoint.ACTION;
        const requestConfig = this._buildRequestConfig(endpoint, payload, clientRequestId);

        return this._executeRequest(requestConfig);
    }

    private _buildRequestConfig(
        endpoint: RestEndpoint,
        payload: Record<string, any>,
        clientRequestId: string
    ): RequestInit & { url: string } {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };

        if (endpoint === RestEndpoint.INIT) {
            return {
                url: `${this._baseUrl}${endpoint}?clientRequestId=${encodeURIComponent(clientRequestId)}&configId=${this._gameName}`,
                method: 'GET',
                headers,
                cache: 'no-store',
            };
        }

        headers['x-session-id'] = payload.sessionId;
        const { sessionId, ...bodyPayload } = payload;

        return {
            url: `${this._baseUrl}${endpoint}?configId=${this._gameName}`,
            method: 'POST',
            headers,
            body: JSON.stringify({
                ...bodyPayload,
                clientRequestId,
            }),
            cache: 'no-store',
        };
    }

    private async _executeRequest(
        config: RequestInit & { url: string }
    ): Promise<TransportResponse> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this._timeout);

        try {
            const { url, ...fetchConfig } = config;
            const response = await fetch(url, {
                ...fetchConfig,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data: TransportResponse = await response.json();

            if (!data.requestType || !data.identity) {
                console.warn('[RestService] Response missing required fields:', data);
            }

            return data;
        } catch (error) {
            clearTimeout(timeoutId);

            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    throw new Error(`Request timeout after ${this._timeout}ms`);
                }
                throw error;
            }

            throw new Error('Unknown error occurred during REST request');
        }
    }
}
